import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['hero', 'world', 'lore', 'web3', 'roadmap', 'investor', 'team', 'cta'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-[#0a0a0f] text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-black/80 backdrop-blur-xl shadow-lg shadow-violet-500/10' : 'bg-transparent'}`}>
        <div className="max-w-[1600px] mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://static.readdy.ai/image/4a2d865f91dc182deaf7525e0752caf1/1760814152fa1b3bc6d44fd6f78c1102.png" alt="Arkania Logo" className="w-12 h-12 object-contain" />
            <span className="text-2xl font-bold tracking-wider text-white" style={{ textShadow: '0 0 20px rgba(139, 92, 246, 0.5)' }}>ARKANIA</span>
          </div>
          
          <div className="hidden md:flex items-center gap-12">
            <button onClick={() => scrollToSection('world')} className="text-sm uppercase tracking-widest text-white/80 hover:text-violet-400 transition-colors duration-300 whitespace-nowrap">World</button>
            <button onClick={() => scrollToSection('web3')} className="text-sm uppercase tracking-widest text-white/80 hover:text-violet-400 transition-colors duration-300 whitespace-nowrap">Vision</button>
            <button onClick={() => scrollToSection('roadmap')} className="text-sm uppercase tracking-widest text-white/80 hover:text-violet-400 transition-colors duration-300 whitespace-nowrap">Roadmap</button>
            <button onClick={() => scrollToSection('team')} className="text-sm uppercase tracking-widest text-white/80 hover:text-violet-400 transition-colors duration-300 whitespace-nowrap">Team</button>
          </div>

          <button onClick={() => scrollToSection('cta')} className="px-8 py-3 rounded-full bg-violet-600 border-2 border-red-600 text-white font-semibold hover:bg-violet-700 hover:shadow-lg hover:shadow-red-600/50 transition-all duration-300 flex items-center gap-2 whitespace-nowrap">
            <span>Investor Portal</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f887f1657428fa5cd9a4218ebc3762ca.jpg" 
            alt="Arkania Universe" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        </div>

        {/* Animated Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-violet-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
                opacity: 0.3 + Math.random() * 0.4
              }}
            ></div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-7xl md:text-8xl lg:text-9xl font-bold mb-8 leading-tight" style={{ 
            fontFamily: 'Cinzel, serif',
            background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 50%, #fbbf24 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 80px rgba(139, 92, 246, 0.5)'
          }}>
            THE UNIVERSE OF ARKANIA
          </h1>
          
          <p className="text-2xl md:text-3xl mb-6 text-white/90 font-light tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
            A living anime universe built for storytelling, technology, and limitless expansion
          </p>
          
          <p className="text-lg md:text-xl mb-12 text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Arkania is a next-generation fictional universe combining anime aesthetics, deep lore, and scalable digital ecosystems. An original IP designed for long-term growth across games, digital series, and interactive experiences.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button onClick={() => scrollToSection('lore')} className="px-10 py-5 rounded-full border-2 border-white text-white font-semibold text-lg hover:bg-white hover:text-black hover:shadow-lg hover:shadow-violet-500/50 transition-all duration-300 whitespace-nowrap">
              Explore the World
            </button>
            <button onClick={() => scrollToSection('investor')} className="px-10 py-5 rounded-full bg-red-600 border-2 border-yellow-500 text-white font-semibold text-lg hover:bg-red-700 hover:shadow-lg hover:shadow-yellow-500/50 transition-all duration-300 flex items-center gap-3 whitespace-nowrap">
              <i className="ri-lock-line"></i>
              <span>Investor Access</span>
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-violet-400"></i>
        </div>
      </section>

      {/* World Introduction */}
      <section id="world" className="relative py-32 bg-gradient-to-b from-[#0a0a0f] to-black">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid md:grid-cols-5 gap-16 items-center">
            <div className="md:col-span-2 relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-500 to-transparent"></div>
              
              <div className="inline-block px-4 py-2 rounded-full bg-violet-600/20 border border-violet-500/30 text-violet-400 text-sm font-semibold mb-6">
                <i className="ri-circle-fill text-xs mr-2"></i>
                ORIGINAL IP
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight" style={{ fontFamily: 'Cinzel, serif' }}>
                A Universe Built for Eternity
              </h2>
              
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Arkania is not just a story—it's a platform for growth and innovation. Designed as a long-term intellectual property, it blends epic storytelling with rich anime-inspired visuals and a modular structure ready to expand.
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                From games to digital series, from interactive experiences to immersive worlds, Arkania is built to evolve, adapt, and captivate audiences for generations to come.
              </p>
            </div>

            <div className="md:col-span-3">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-violet-500/20 hover:shadow-violet-500/40 transition-shadow duration-500">
                <img 
                  src="https://readdy.ai/api/search-image?query=epic%20anime%20style%20fantasy%20landscape%20with%20floating%20kingdoms%20and%20crystalline%20towers%20suspended%20in%20the%20sky%20surrounded%20by%20glowing%20violet%20and%20blue%20magical%20energy%20streams%20ancient%20architecture%20blending%20with%20futuristic%20technology%20dramatic%20clouds%20and%20ethereal%20light%20beams%20creating%20a%20majestic%20and%20mysterious%20atmosphere%20cinematic%20digital%20art%20with%20rich%20details&width=1200&height=800&seq=arkania-world-001&orientation=landscape" 
                  alt="The Eternal Kingdoms" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-8">
                  <div className="flex items-center gap-3 text-white">
                    <i className="ri-map-pin-line text-2xl text-violet-400"></i>
                    <span className="text-xl font-semibold">The Eternal Kingdoms</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lore Showcase */}
      <section id="lore" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(139, 92, 246, 0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Cinzel, serif' }}>
              The World of <span className="text-yellow-500">Arkania</span>
            </h2>
            <p className="text-2xl text-violet-400 italic" style={{ fontFamily: 'Cinzel, serif' }}>
              Where magic collides with ancient technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Crimson Order */}
            <div className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-red-900/40 to-red-950/40 border border-red-500/30 p-8 hover:border-red-500/60 hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-red-600/20 border-2 border-red-500 group-hover:animate-pulse">
                  <i className="ri-sword-line text-4xl text-red-400"></i>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4 text-center">The Crimson Order</h3>
                
                <p className="text-gray-300 leading-relaxed text-center">
                  Ancient warriors bound by blood oaths, wielding crimson flames that burn through reality itself. They guard the forbidden gates between worlds, where light and shadow merge into eternal twilight.
                </p>
              </div>
            </div>

            {/* Azure Collective */}
            <div className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-900/40 to-blue-950/40 border border-blue-500/30 p-8 hover:border-blue-500/60 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-blue-600/20 border-2 border-blue-500 group-hover:animate-pulse">
                  <i className="ri-brain-line text-4xl text-blue-400"></i>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4 text-center">The Azure Collective</h3>
                
                <p className="text-gray-300 leading-relaxed text-center">
                  Masters of consciousness and digital realms, they transcend physical form to navigate the infinite networks of thought. Their minds are libraries of forgotten civilizations and futures yet unwritten.
                </p>
              </div>
            </div>

            {/* Golden Architects */}
            <div className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-yellow-900/40 to-yellow-950/40 border border-yellow-500/30 p-8 hover:border-yellow-500/60 hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-yellow-600/20 border-2 border-yellow-500 group-hover:animate-pulse">
                  <i className="ri-compass-3-line text-4xl text-yellow-400"></i>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4 text-center">The Golden Architects</h3>
                
                <p className="text-gray-300 leading-relaxed text-center">
                  Builders of impossible structures that defy physics and time. They shape reality with sacred geometry, creating sanctuaries where past and future exist simultaneously in golden harmony.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Web3 Technology */}
      <section id="web3" className="relative py-32 bg-gradient-to-b from-black to-[#0a0a0f]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-block px-4 py-2 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-sm font-semibold mb-6 whitespace-nowrap">
                FUTURE-READY ECOSYSTEM
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold mb-12" style={{ fontFamily: 'Cinzel, serif' }}>
                Built for the Digital Age
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-violet-600/20 border-2 border-violet-500">
                    <i className="ri-team-line text-2xl text-violet-400"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Community-Driven</h3>
                    <p className="text-gray-400 leading-relaxed">
                      A vibrant ecosystem where fans, creators, and investors shape the future of Arkania together through collaborative storytelling and shared ownership.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-blue-600/20 border-2 border-blue-500">
                    <i className="ri-shield-check-line text-2xl text-blue-400"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Digital Ownership</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Empowering true ownership of digital assets, characters, and experiences within the Arkania universe through blockchain technology.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-red-600/20 border-2 border-red-500">
                    <i className="ri-infinity-line text-2xl text-red-400"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Scalable Architecture</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Modular design allowing seamless expansion into new media formats, platforms, and experiences without compromising the core narrative.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-yellow-600/20 border-2 border-yellow-500">
                    <i className="ri-cube-line text-2xl text-yellow-400"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Web3 Ready</h3>
                    <p className="text-gray-400 leading-relaxed">
                      Prepared for integration with NFTs, tokens, and decentralized platforms, positioning Arkania at the forefront of digital innovation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://static.readdy.ai/image/4a2d865f91dc182deaf7525e0752caf1/d9ba794a349eae0a4a1c296317145500.png" 
                  alt="Web3 Technology" 
                  className="w-full h-auto rounded-2xl"
                />
              </div>
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-violet-600/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="relative py-32 bg-black">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-bold mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
              Vision & Roadmap
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-violet-500 to-transparent mx-auto"></div>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 via-blue-600 to-yellow-600 transform -translate-y-1/2"></div>

            <div className="grid md:grid-cols-4 gap-8">
              {/* Phase 1 */}
              <div className="relative group">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-violet-600 rounded-full border-4 border-black hidden md:block group-hover:scale-150 transition-transform duration-300"></div>
                
                <div className="bg-gradient-to-br from-violet-900/40 to-black border-2 border-violet-500/50 rounded-2xl p-8 hover:border-violet-500 hover:shadow-2xl hover:shadow-violet-500/30 transition-all duration-500 mt-12 md:mt-24">
                  <div className="text-6xl font-bold text-violet-500/30 mb-4" style={{ fontFamily: 'Cinzel, serif' }}>01</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Foundation</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-violet-400 mt-1"></i>
                      <span>Core worldbuilding & lore development</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-violet-400 mt-1"></i>
                      <span>Brand identity & visual design</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-violet-400 mt-1"></i>
                      <span>Website launch & investor outreach</span>
                    </li>
                  </ul>
                  <div className="mt-6 text-sm text-violet-400 font-semibold">Q1-Q2 2025</div>
                </div>
              </div>

              {/* Phase 2 */}
              <div className="relative group">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-blue-600 rounded-full border-4 border-black hidden md:block group-hover:scale-150 transition-transform duration-300"></div>
                
                <div className="bg-gradient-to-br from-blue-900/40 to-black border-2 border-blue-500/50 rounded-2xl p-8 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-500 mt-12 md:mt-32">
                  <div className="text-6xl font-bold text-blue-500/30 mb-4" style={{ fontFamily: 'Cinzel, serif' }}>02</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Community</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-400 mt-1"></i>
                      <span>Social media & Discord community</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-400 mt-1"></i>
                      <span>Early concept art & character reveals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-400 mt-1"></i>
                      <span>Partnerships & collaborations</span>
                    </li>
                  </ul>
                  <div className="mt-6 text-sm text-blue-400 font-semibold">Q3 2025</div>
                </div>
              </div>

              {/* Phase 3 */}
              <div className="relative group">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-red-600 rounded-full border-4 border-black hidden md:block group-hover:scale-150 transition-transform duration-300"></div>
                
                <div className="bg-gradient-to-br from-red-900/40 to-black border-2 border-red-500/50 rounded-2xl p-8 hover:border-red-500 hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-500 mt-12 md:mt-24">
                  <div className="text-6xl font-bold text-red-500/30 mb-4" style={{ fontFamily: 'Cinzel, serif' }}>03</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Expansion</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-red-400 mt-1"></i>
                      <span>Interactive web experiences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-red-400 mt-1"></i>
                      <span>Digital collectibles & NFT launch</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-red-400 mt-1"></i>
                      <span>Game prototype development</span>
                    </li>
                  </ul>
                  <div className="mt-6 text-sm text-red-400 font-semibold">Q4 2025 - Q1 2026</div>
                </div>
              </div>

              {/* Phase 4 */}
              <div className="relative group">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-yellow-600 rounded-full border-4 border-black hidden md:block group-hover:scale-150 transition-transform duration-300"></div>
                
                <div className="bg-gradient-to-br from-yellow-900/40 to-black border-2 border-yellow-500/50 rounded-2xl p-8 hover:border-yellow-500 hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-500 mt-12 md:mt-32">
                  <div className="text-6xl font-bold text-yellow-500/30 mb-4" style={{ fontFamily: 'Cinzel, serif' }}>04</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Monetization</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-yellow-400 mt-1"></i>
                      <span>Full game & series production</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-yellow-400 mt-1"></i>
                      <span>Merchandise & licensing deals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-yellow-400 mt-1"></i>
                      <span>Cross-platform ecosystem launch</span>
                    </li>
                  </ul>
                  <div className="mt-6 text-sm text-yellow-400 font-semibold">Q2 2026+</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Opportunity */}
      <section id="investor" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0f] via-violet-950/20 to-black"></div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-8">
          <div className="grid md:grid-cols-5 gap-16 items-center">
            <div className="md:col-span-3">
              <h2 className="text-5xl md:text-7xl font-bold mb-12" style={{ fontFamily: 'Cinzel, serif' }}>
                Why Invest in Arkania?
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <i className="ri-checkbox-circle-fill text-3xl text-red-500 flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Original Intellectual Property</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      A completely original universe with no existing dependencies, offering full creative control and unlimited expansion potential across all media formats.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <i className="ri-checkbox-circle-fill text-3xl text-red-500 flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Anime Market Growth</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      The global anime industry is valued at over $31 billion and growing rapidly, with increasing demand for original content and immersive experiences.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <i className="ri-checkbox-circle-fill text-3xl text-red-500 flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Cross-Media Expansion Strategy</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      Designed from the ground up for seamless adaptation into games, animated series, comics, merchandise, and interactive digital experiences.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <i className="ri-checkbox-circle-fill text-3xl text-red-500 flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Strong Brand Identity</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      A distinctive visual style and compelling narrative foundation that resonates with the target demographic of 18-35 year old anime enthusiasts and gamers.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <i className="ri-checkbox-circle-fill text-3xl text-red-500 flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Early Investor Positioning</h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      Ground-floor opportunity to be part of a universe from its inception, with significant upside potential as the IP grows and expands globally.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="relative">
                <div className="bg-gradient-to-br from-violet-900/40 to-black border-2 border-violet-500/50 rounded-3xl p-12 text-center hover:border-violet-500 hover:shadow-2xl hover:shadow-violet-500/30 transition-all duration-500">
                  <div className="text-violet-400 text-lg font-semibold mb-4 uppercase tracking-wider">Market Potential</div>
                  <div className="text-7xl font-bold text-white mb-4" style={{ 
                    fontFamily: 'Cinzel, serif',
                    textShadow: '0 0 40px rgba(251, 191, 36, 0.5)'
                  }}>
                    $1B+
                  </div>
                  <div className="text-gray-400 text-sm">Global Anime Industry Value</div>
                </div>

                <div className="mt-8 bg-gradient-to-br from-yellow-900/40 to-black border-2 border-yellow-500/50 rounded-3xl p-12 text-center hover:border-yellow-500 hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-500">
                  <div className="text-yellow-400 text-lg font-semibold mb-4 uppercase tracking-wider">Annual Growth</div>
                  <div className="text-7xl font-bold text-white mb-4" style={{ 
                    fontFamily: 'Cinzel, serif',
                    textShadow: '0 0 40px rgba(139, 92, 246, 0.5)'
                  }}>
                    9.8%
                  </div>
                  <div className="text-gray-400 text-sm">Projected CAGR 2024-2030</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <button onClick={() => scrollToSection('cta')} className="px-12 py-5 rounded-full bg-red-600 border-2 border-yellow-500 text-white text-xl font-bold hover:bg-red-700 hover:shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 inline-flex items-center gap-3 whitespace-nowrap">
              <span>Request Investment Deck</span>
              <i className="ri-arrow-right-line text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="relative py-32 bg-gradient-to-b from-black to-violet-950/20">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-bold mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
              The Visionaries
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Creator 1 */}
            <div className="group text-center">
              <div className="relative mb-6 mx-auto w-64 h-64">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-violet-900 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=anime%20style%20portrait%20of%20a%20visionary%20creative%20director%20with%20mysterious%20violet%20glowing%20eyes%20wearing%20futuristic%20dark%20clothing%20with%20ethereal%20energy%20surrounding%20them%20cinematic%20lighting%20and%20dramatic%20composition%20digital%20art%20with%20rich%20purple%20and%20blue%20tones%20professional%20and%20enigmatic%20atmosphere&width=400&height=400&seq=arkania-team-001&orientation=squarish" 
                  alt="Creative Director" 
                  className="relative z-10 w-full h-full object-cover rounded-2xl border-2 border-violet-500 group-hover:border-violet-400 group-hover:scale-105 transition-all duration-500"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Kaelen Voss</h3>
              <p className="text-violet-400 italic mb-4" style={{ fontFamily: 'Cinzel, serif' }}>Creative Director</p>
              <p className="text-gray-400 leading-relaxed">
                Driven by the belief that stories can transcend reality, Kaelen envisions Arkania as a living canvas where imagination meets innovation, creating experiences that resonate across generations.
              </p>
            </div>

            {/* Creator 2 */}
            <div className="group text-center">
              <div className="relative mb-6 mx-auto w-64 h-64">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-900 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=anime%20style%20portrait%20of%20a%20technical%20architect%20with%20glowing%20blue%20digital%20patterns%20and%20holographic%20interfaces%20around%20them%20wearing%20sleek%20futuristic%20attire%20with%20cyberpunk%20elements%20cinematic%20lighting%20and%20professional%20composition%20digital%20art%20with%20blue%20and%20cyan%20tones%20innovative%20and%20focused%20atmosphere&width=400&height=400&seq=arkania-team-002&orientation=squarish" 
                  alt="Technical Architect" 
                  className="relative z-10 w-full h-full object-cover rounded-2xl border-2 border-blue-500 group-hover:border-blue-400 group-hover:scale-105 transition-all duration-500"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Aria Nexus</h3>
              <p className="text-blue-400 italic mb-4" style={{ fontFamily: 'Cinzel, serif' }}>Technical Architect</p>
              <p className="text-gray-400 leading-relaxed">
                Merging art with technology, Aria builds the digital infrastructure that brings Arkania to life, ensuring scalability and innovation remain at the core of every expansion.
              </p>
            </div>

            {/* Creator 3 */}
            <div className="group text-center">
              <div className="relative mb-6 mx-auto w-64 h-64">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=anime%20style%20portrait%20of%20a%20world%20builder%20surrounded%20by%20golden%20magical%20symbols%20and%20ancient%20scrolls%20with%20mystical%20energy%20flowing%20around%20them%20wearing%20elegant%20robes%20with%20gold%20accents%20cinematic%20lighting%20and%20epic%20composition%20digital%20art%20with%20warm%20golden%20tones%20wise%20and%20creative%20atmosphere&width=400&height=400&seq=arkania-team-003&orientation=squarish" 
                  alt="World Builder" 
                  className="relative z-10 w-full h-full object-cover rounded-2xl border-2 border-yellow-500 group-hover:border-yellow-400 group-hover:scale-105 transition-all duration-500"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Zephyr Lumina</h3>
              <p className="text-yellow-400 italic mb-4" style={{ fontFamily: 'Cinzel, serif' }}>World Builder</p>
              <p className="text-gray-400 leading-relaxed">
                Crafting the intricate lore and mythology of Arkania, Zephyr weaves narratives that connect past, present, and future into a cohesive tapestry of endless possibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=epic%20cinematic%20anime%20style%20landscape%20with%20a%20glowing%20golden%20and%20violet%20horizon%20over%20a%20vast%20mystical%20world%20featuring%20floating%20islands%20ancient%20ruins%20and%20ethereal%20light%20beams%20piercing%20through%20dramatic%20clouds%20creating%20an%20inspiring%20and%20visionary%20atmosphere%20with%20rich%20purple%20gold%20and%20blue%20color%20palette%20ultra%20detailed%20digital%20art&width=1920&height=1080&seq=arkania-cta-bg-001&orientation=landscape" 
            alt="Join Arkania" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h2 className="text-6xl md:text-8xl font-bold mb-8 leading-tight" style={{ 
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 60px rgba(139, 92, 246, 0.6)'
          }}>
            Shape a Universe With No Limits
          </h2>
          
          <p className="text-2xl md:text-3xl mb-12 text-gray-200 font-light leading-relaxed">
            Join us in building the future of transmedia storytelling
          </p>

          <button className="px-16 py-6 rounded-full bg-red-600 border-2 border-yellow-500 text-white text-2xl font-bold hover:bg-red-700 hover:shadow-2xl hover:shadow-yellow-500/60 transition-all duration-300 inline-flex items-center gap-4 whitespace-nowrap">
            <span>Contact for Investment Opportunities</span>
            <i className="ri-arrow-right-line text-3xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-black rounded-t-[32px] border-t border-violet-500/30">
        <div className="max-w-[1400px] mx-auto px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <img src="https://static.readdy.ai/image/4a2d865f91dc182deaf7525e0752caf1/1760814152fa1b3bc6d44fd6f78c1102.png" alt="Arkania Logo" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold tracking-wider text-white">ARKANIA</span>
            </div>

            <div className="flex items-center gap-8">
              <a href="#" className="text-violet-400 hover:text-white transition-colors duration-300" aria-label="Twitter">
                <i className="ri-twitter-x-line text-2xl"></i>
              </a>
              <a href="#" className="text-violet-400 hover:text-white transition-colors duration-300" aria-label="Discord">
                <i className="ri-discord-line text-2xl"></i>
              </a>
              <a href="#" className="text-violet-400 hover:text-white transition-colors duration-300" aria-label="LinkedIn">
                <i className="ri-linkedin-line text-2xl"></i>
              </a>
              <a href="#" className="text-violet-400 hover:text-white transition-colors duration-300" aria-label="Medium">
                <i className="ri-medium-line text-2xl"></i>
              </a>
            </div>

            <div className="text-gray-500 text-sm text-center md:text-right">
              <p>© 2025 The Universe of Arkania. All Rights Reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 transition-colors duration-300 mt-2 inline-block whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
